
import { Component, Vue } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import { handleAllErrors } from '@/utils/errorHandling';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { Validate } from '@/types';
import { validateStudentFields, getSchoolDeliveryOptions } from '@/utils/helpers';
import StudentFormFields from '@/components/profile/students/StudentFormFields.vue';
import 'vue-select/dist/vue-select.css';
import '@/validation-rules';

const namespace: string = 'auth';

@Component<StudentForm>({
	components: {
		ValidationProvider,
		ValidationObserver,
		StudentFormFields
	}
})
export default class StudentForm extends Vue {
	@Action('addStudent', { namespace }) private addStudent!: (payload: CheckoutDeliveryInfo) => Promise<void>;
	@Action('editStudent', { namespace }) private editStudent!: (payload: CheckoutDeliveryInfo) => Promise<void>;
	@Action('deleteStudent', { namespace }) private deleteStudent!: (payload: CheckoutDeliveryInfo) => Promise<void>;
	@Action('fetchGroupLocations', { namespace: 'restaurant' }) private fetchGroupLocations!: (restaurantId: string) => Promise<void>;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;
	@Getter('getRestaurantDeliveryInfo', { namespace: 'restaurant' }) private restaurantDeliveryInfo!: SchoolDelivery;
	@Getter('getRestaurantGroupLocations', { namespace: 'restaurant' }) private groupLocations!: any[];
	@Getter('getUser', { namespace }) private user!: UserInfo;
	@Getter('showLoginAsBanner', { namespace }) private showLoginAsBanner!: boolean;
	@Getter('getUserStudents', { namespace }) private userStudents!: UserStudent[];
	$refs!: { observer: Validate };

	private dataFetched: boolean = false;
	private selectedLocationDeliveryInfo: SchoolDelivery | null = null;
	private student: Partial<UserStudent> = {
		name: '',
		school: '',
		grade: '',
		classroom: '',
		teacher: '',
		extra: '',
		id: '',
		type: 'school',
		token: '',
		userId: '',
		restaurantId: '',
		locationSlug: ''
	};

	/**
	 * This getter fetches the school delivery information.
	 * It is used to populate the school's dependant fields
	 *
	 * @return {SchoolDeliveryOptions}
	 */
	private get schoolDeliveryOptions(): SchoolDeliveryOptions {
		const deliveryInfo = this.selectedLocationDeliveryInfo || this.restaurantDeliveryInfo;
		return getSchoolDeliveryOptions(deliveryInfo, this.student.school);
	}

	/**
	 * Check if the Student form is open in Edit mode.
	 *
	 * @return {boolean}
	 */
	private get isEditMode(): boolean {
		return !!this.$route.params.id;
	}

	/**
	 * Fetch group locations if needed and load student data in edit mode
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		if (!this.groupLocations.length) {
			try {
				await this.fetchGroupLocations(this.restaurant.id);
			}
			catch (error) {
				handleAllErrors(this.$t('profile.students.select_modal.error_loading_locations'), error);
			}
		}

		if (this.isEditMode) {
			try {
				this.loadStudentData();
			}
			catch (error) {
				handleAllErrors(this.$t('profile.students.error_fetch_students'), error);
			}
		}

		this.dataFetched = true;
	}

	/**
	 * Scroll to top on mount
	 *
	 * @return {void}
	 */
	private mounted(): void {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	/**
	 * Handle location (restaurant) selection change
	 * Updates location-specific data; field resets and auto-selection handled by StudentFormFields
	 *
	 * @param {string} restaurantId
	 * @return {void}
	 */
	private onLocationChange(restaurantId: string): void {
		const selectedLocation = this.groupLocations.find(loc => loc.app8_restaurant === restaurantId);
		if (selectedLocation) {
			this.$set(this.student, 'restaurantId', restaurantId);
			this.$set(this.student, 'locationSlug', selectedLocation.slug);
			this.selectedLocationDeliveryInfo = selectedLocation.delivery || null;
		}
	}

	/**
	 * If the student id is part of the route, display the student's data.
	 * Validates the student's fields against their location's current delivery configuration.
	 *
	 * @return {void}
	 */
	private loadStudentData(): void {
		const studentId = this.$route.params.id;
		const student = this.userStudents.find(student => student.id?.toString() === studentId);
		this.student = student || {};

		// If student has a restaurantId, find and set the corresponding location's delivery info
		if (this.student.restaurantId && this.groupLocations.length) {
			const selectedLocation = this.groupLocations.find(loc => loc.app8_restaurant === this.student.restaurantId);
			if (selectedLocation) {
				this.selectedLocationDeliveryInfo = selectedLocation.delivery || null;
			}
		}

		// Validate student fields against the correct location's delivery info
		// This ensures fields are still valid if the restaurant's configuration changed
		const deliveryInfo = this.selectedLocationDeliveryInfo || this.restaurantDeliveryInfo;
		const schoolDeliveryOptions = getSchoolDeliveryOptions(deliveryInfo, this.student.school);
		validateStudentFields({ student: this.student, restaurantDeliveryInfo: deliveryInfo, schoolDeliveryOptions });
	}

	/**
	 * Handles on form submit.
	 *
	 * @return {Promise<void>}
	 */
	private async submit(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if (isValid) {
			await this.saveStudent();
		}
	}

	/**
	 * Saves the student.
	 * If an id is provided, it would edit that student.
	 * If no id is provided, it would create a new student.
	 *
	 * @return {Promise<void>}
	 */
	private async saveStudent(): Promise<void> {
		try {
			this.dataFetched = false;
			const payload: UserStudent = {
				userId: this.user.id,
				token: this.user.token,
				name: this.student.name ?? '',
				type: this.restaurant.k12 ? 'school' : '',
				school: this.student.school ?? '',
				grade: this.student.grade ?? '',
				classroom: this.student.classroom ?? '',
				teacher: this.student.teacher ?? '',
				extra: this.student.extra ?? '',
				restaurantId: this.student.restaurantId ?? '',
				locationSlug: this.student.locationSlug ?? ''
			};

			if (this.isEditMode) {
				payload.id = this.student.id;
				await this.editStudent(payload);
			}
			else {
				await this.addStudent(payload);
			}

			this.redirectToStudentsPage();
		}
		catch (error) {
			handleAllErrors(this.$t('profile.students.error_save_student'), error);
		}
		finally {
			this.dataFetched = true;
		}
	}

	/**
	 * Deletes students.
	 *
	 * @return {Promise<void>}
	 */
	private async removeStudent(): Promise<void> {
		try {
			this.dataFetched = false;
			const payload: UserStudent = {
				id: this.student.id!,
				userId: this.user.id,
				token: this.user.token,
				type: 'school'
			};
			await this.deleteStudent(payload);
			this.redirectToStudentsPage();
		}
		catch (error) {
			handleAllErrors(this.$t('profile.students.error_delete_student'), error);
		}
		finally {
			this.dataFetched = true;
		}
	}

	private redirectToStudentsPage(): void {
		this.$router.push('/profile/students').catch(() => {});
	}
}
